// src/flow/flowAdapter.ts
import { FlowTypes } from 'store/constants';
import { formDepositGroups, formWithdrawalGroups } from 'store/formatters';
import {
  depositAPI,
  INIT_TAG as INIT_TAG_PAYMENT,
  SELECT_TAG as SELECT_TAG_PAYMENT,
  SUBMIT_TAG,
  useGetDepositMethodsQuery,
  useLazyGetDepositMethodsQuery,
} from 'store/services/depositAPI';
import {
  INIT_TAG as INIT_TAG_PAYOUT,
  SELECT_TAG as SELECT_TAG_PAYOUT,
  useGetWithdrawalMethodsQuery,
  useLazyGetWithdrawalMethodsQuery,
  withdrawalAPI,
} from 'store/services/withdrawalAPI';
import { WIDGET_API } from 'utils/constants';
import { addPwPrefixToBaseUrl } from 'utils/pwContext';

import type {
  PayHubCashierContractsPaymentsInitPaymentGroupsResponse,
  PayHubCashierContractsPayoutsInitPayoutGroupsResponse,
} from '../../store/models';
import { PAYMENT_EMPTY_DATA, PAYOUT_EMPTY_DATA } from './constants';

const createMetricUrl = (endpoint: string) => {
  const baseUrl = `${WIDGET_API}/${endpoint}/metrics`;
  return addPwPrefixToBaseUrl(baseUrl);
};

export const flowAdapter = {
  [FlowTypes.deposit]: {
    useInitQuery: useGetDepositMethodsQuery,
    useLazyInitQuery: useLazyGetDepositMethodsQuery,
    initiate: depositAPI.endpoints.getDepositMethods.initiate,
    formatter: (
      data?: PayHubCashierContractsPaymentsInitPaymentGroupsResponse,
      args?: { payGroupId?: number; skipRedirectMethod?: boolean; isOnRamp?: boolean; hasOnRampCurrency?: boolean },
      userAgent?: string,
      appChannel?: string,
      initCurrency?: string,
    ) => formDepositGroups(data, args, userAgent, appChannel, initCurrency),
    invalidate: () =>
      depositAPI.util.invalidateTags([
        {
          type: INIT_TAG_PAYMENT,
        },
        {
          type: SELECT_TAG_PAYMENT,
        },
        {
          type: SUBMIT_TAG,
        },
      ]),
    tracking: {
      metricUrl: createMetricUrl('payments'),
      metricName: 'init_call_duration',
      trackLazy: false,
    },
    emptyData: PAYMENT_EMPTY_DATA,
  },

  [FlowTypes.withdrawal]: {
    useInitQuery: useGetWithdrawalMethodsQuery,
    useLazyInitQuery: useLazyGetWithdrawalMethodsQuery,
    initiate: withdrawalAPI.endpoints.getWithdrawalMethods.initiate,
    formatter: (
      data?: PayHubCashierContractsPayoutsInitPayoutGroupsResponse,
      args?: { payGroupId?: number; skipRedirectMethod?: boolean; isOnRamp?: boolean; hasOnRampCurrency?: boolean },
      userAgent?: string,
      appChannel?: string,
      initCurrency?: string,
    ) => formWithdrawalGroups(data, userAgent, appChannel, initCurrency, args),
    invalidate: () =>
      withdrawalAPI.util.invalidateTags([
        {
          type: INIT_TAG_PAYOUT,
        },
        {
          type: SELECT_TAG_PAYOUT,
        },
      ]),
    tracking: {
      metricUrl: createMetricUrl('payouts'),
      metricName: 'init_call_duration',
      trackLazy: false,
    },
    emptyData: PAYOUT_EMPTY_DATA,
  },
} as const;

export type FlowAdapter = (typeof flowAdapter)[keyof typeof flowAdapter];
